import { Action, Card, Modal, OrderLineItem } from 'corso-admin-ui';
import { useState } from 'react';
import ProductReplacement from '~/features/claimReview/components/resolutionModifiers/ProductReplacement';
import { useShippingClaimLineItemAdd } from '~/features/shippingClaimReview/hooks/useShippingClaimLineItemAdd';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { SelectedVariant } from '~/types';
import { useEnabledReorderLocations } from '../hooks/useShippingClaimLookupConfig';

export function AddShippingClaimLineItem({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) {
  const { shippingClaimLineItems } = useShippingClaim();
  const [quickSearchKey, setQuickSearchKey] = useState('');
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>(
    [],
  );
  const { mutateAsync: addLineItem, isPending } = useShippingClaimLineItemAdd();

  const quickSearchItem = shippingClaimLineItems.find(
    (li) => `${li.kind}-${li.id}` === quickSearchKey,
  );

  const { locations, provider } = useEnabledReorderLocations();

  const closeAndReset = () => {
    setSelectedVariants([]);
    setQuickSearchKey('');
    onClose();
  };

  const onSubmit = () =>
    addLineItem(
      selectedVariants.map(({ idFromPlatform, quantity }) => ({
        idFromPlatform,
        quantity,
      })),
    ).then(closeAndReset);

  return (
    <Modal
      title="Add Line Item"
      show={show}
      size="lg"
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            disabled={isPending || selectedVariants.length === 0}
            loading={isPending}
            onClick={() => {
              onSubmit().catch((error: unknown) =>
                console.error('Failed to add line items', error),
              );
            }}
          >
            Add
          </Action>
        </>
      }
    >
      <div className="space-y-4">
        {shippingClaimLineItems.length > 0 && (
          <div className="space-y-2">
            <Card.Heading>Quick Search</Card.Heading>
            <ul className="divide-y divide-neutral-200">
              {shippingClaimLineItems.map((li) => {
                const key = `${li.kind}-${li.id}`;
                const isSearching = key === quickSearchKey;
                const name = li.name ?? li.sku ?? 'Line Item';

                return (
                  <li key={key} className="flex items-center gap-2 py-2">
                    <div className="flex-1">
                      <OrderLineItem
                        size="sm"
                        src={li.imgUrl}
                        sku={li.sku}
                        title={li.name}
                        quantity={li.quantity}
                        options={li.optionsFromPlatform}
                      />
                    </div>
                    <Action
                      icon={isSearching ? 'searchSolid' : 'search'}
                      variant="ghost"
                      pressed
                      aria-pressed={isSearching}
                      accessibilityLabel={
                        isSearching
                          ? `Clear search for ${name}`
                          : `Search for ${name}`
                      }
                      onClick={() => setQuickSearchKey(isSearching ? '' : key)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <ProductReplacement
          // the search only reads its term on mount, so remount it whenever the pick changes
          key={quickSearchKey}
          defaultSearchTerm={
            quickSearchItem?.name ?? quickSearchItem?.sku ?? ''
          }
          allLocationsLabel={
            provider === 'all' ? 'All Locations' : 'Reorder Locations'
          }
          selectedVariants={selectedVariants}
          onChange={setSelectedVariants}
          locations={locations}
        />
      </div>
    </Modal>
  );
}
